import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, BookOpen, Calendar, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getImageStyle } from '../components/ImagePositionEditor';
import axios from 'axios';
import { normalizeHomeSections, homeSectionHref } from '../lib/homeSections';
import HomeBookCard, { HOME_BOOK_DEFAULTS } from '../components/HomeBookCard';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [homeSections, setHomeSections] = useState(() => normalizeHomeSections({}));
  const [bookInfo, setBookInfo] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [siteInfo, setSiteInfo] = useState(null);
  const [homeHero, setHomeHero] = useState(null);
  const [sections, setSections] = useState(null);
  const [testimonials, setTestimonials] = useState([
    {
      id: '1',
      name: 'Sarah M.',
      text: 'Lindsey\'s guidance through my explant journey was invaluable. She understood exactly what I was going through and provided both emotional and practical support.',
      rating: 5
    },
    {
      id: '2',
      name: 'Jennifer K.',
      text: 'Working with Lindsey gave me the confidence and knowledge I needed to move forward with my explant surgery. Her support during recovery was life-changing.',
      rating: 5
    },
    {
      id: '3',
      name: 'Michelle T.',
      text: 'I can\'t thank Lindsey enough for her compassionate coaching. She helped me understand my symptoms and guided me toward healing. Truly grateful!',
      rating: 5
    }
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    axios.get(`${API_URL}/page-content/homepage_sections`).then(res => setHomeSections(normalizeHomeSections(res.data))).catch(() => {});
    // The book request is independent so a temporary failure cannot block the homepage.
    axios.get(`${API_URL}/book/info`).then(res => setBookInfo(res.data)).catch(() => {});
  }, []);

  const fetchData = async () => {
    try {
      const [sessionsRes, siteInfoRes, homeHeroRes, testimonialsRes, sectionsRes] = await Promise.all([
        axios.get(`${API_URL}/sessions`),
        axios.get(`${API_URL}/site-info`),
        axios.get(`${API_URL}/home-hero`),
        axios.get(`${API_URL}/testimonials`),
        axios.get(`${API_URL}/site-sections`)
      ]);
      setSessions(sessionsRes.data.slice(0, 4));
      setSiteInfo(siteInfoRes.data);
      setHomeHero(homeHeroRes.data);
      setTestimonials(testimonialsRes.data);
      setSections(sectionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Set fallback if API fails
      setHomeHero({
        badge: "Explant Surgery Specialist",
        title: "Your Journey to",
        subtitle: "Healing Begins Here",
        description: "Compassionate support and expert guidance for women navigating breast implant illness and the explant journey.",
        heroImage: "https://customer-assets.emergentagent.com/job_0611684f-3585-4c13-8ded-0605552a5675/artifacts/88szxmlf_IMGL4002.jpg"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  const homepage = sections?.homepage || {};
  const bookingEnabled = sections?.booking?.enabled !== false;
  const bookEnabled = sections?.book?.enabled !== false;
  const showBookFeature = bookEnabled && homeHero?.bookCard?.enabled !== false && !!bookInfo;
  const serviceCards = [
    {
      key: 'bookingCard',
      enabled: homepage.bookingCard !== false && bookingEnabled,
      icon: <Calendar className="h-7 w-7 text-sage-700" />,
      iconClass: 'bg-sage-100',
      title: 'Coaching Sessions',
      description: 'Personalized one-on-one support tailored to your unique journey and needs.',
      to: '/booking',
      label: 'Book Now'
    },
    {
      key: 'bookCard',
      enabled: homepage.bookCard !== false && bookEnabled,
      icon: <BookOpen className="h-7 w-7 text-lavender-700" />,
      iconClass: 'bg-lavender-100',
      title: 'My Book',
      description: 'A comprehensive guide for navigating BII and the explant journey.',
      to: '/book',
      label: 'Learn More'
    },
    {
      key: 'downloadsCard',
      enabled: homepage.downloadsCard !== false,
      icon: <Download className="h-7 w-7 text-rose-700" />,
      iconClass: 'bg-rose-100',
      title: 'Free Resources',
      description: 'Download helpful guides, checklists, and tools for your journey.',
      to: '/downloads',
      label: 'Browse Resources'
    },
    {
      key: 'communityCard',
      enabled: homepage.communityCard !== false,
      icon: <Heart className="h-7 w-7 text-amber-700" />,
      iconClass: 'bg-amber-100',
      title: 'Community Support',
      description: 'Join a supportive community of women on similar healing journeys.',
      href: siteInfo?.social_media?.instagram || '#',
      label: 'Connect'
    }
  ].map(card => {
    const config = homeSections[card.key];
    const url = homeSectionHref(config.url || (card.key === 'communityCard' ? siteInfo?.social_media?.instagram : ''));
    return { ...card, enabled: card.enabled && config.enabled !== false, title: config.title, description: config.description, label: config.label, href: url, to: url.startsWith('/') && !config.newTab ? url : null, newTab: config.newTab };
  }).filter(card => card.enabled);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className={`relative py-20 md:py-32 overflow-hidden ${showBookFeature ? 'home-book-hero' : ''}`}>
        <div className="absolute inset-0 bg-gradient-to-br from-sage-50 via-lavender-50 to-cream-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {showBookFeature && <div className="home-book-feature"><HomeBookCard book={bookInfo} settings={{ ...HOME_BOOK_DEFAULTS, ...homeHero?.bookCard }} photo={homeHero?.heroImage} /></div>}
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full text-sm font-medium text-sage-800 border border-sage-200">
                {homeHero?.badge || 'Explant Surgery Specialist'}
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-gray-900 leading-tight">
                {homeHero?.title || 'Your Journey to'}
                <span className="block text-sage-700 mt-2">{homeHero?.subtitle || 'Healing Begins Here'}</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-xl">
                {homeHero?.description || 'Compassionate support and expert guidance for women navigating breast implant illness and the explant journey.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                {bookingEnabled && (
                  <Link to="/booking">
                    <Button size="lg" className="bg-sage-700 hover:bg-sage-800 text-white px-8 py-6 text-lg rounded-xl transition-all duration-300 hover:shadow-lg">
                      Book Your Session
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                )}
                <Link to="/about">
                  <Button size="lg" variant="outline" className="border-2 border-sage-700 text-sage-700 hover:bg-sage-50 px-8 py-6 text-lg rounded-xl transition-all duration-300">
                    Meet Lindsey
                  </Button>
                </Link>
              </div>
            </div>
            {!showBookFeature && <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={homeHero?.heroImage || "https://customer-assets.emergentagent.com/job_0611684f-3585-4c13-8ded-0605552a5675/artifacts/88szxmlf_IMGL4002.jpg"}
                  alt="Lindsey Mayr"
                  className="w-full h-full"
                  style={getImageStyle(homeHero?.heroImagePosition)}
                  onError={(e) => {
                    console.error('Image failed to load:', e.target.src);
                    e.target.src = "https://customer-assets.emergentagent.com/job_0611684f-3585-4c13-8ded-0605552a5675/artifacts/88szxmlf_IMGL4002.jpg";
                  }}
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
                <div className="flex items-center space-x-3">
                  <Heart className="h-8 w-8 text-rose-500" />
                  <div>
                    <p className="text-2xl font-bold text-gray-900">500+</p>
                    <p className="text-sm text-gray-600">Women Supported</p>
                  </div>
                </div>
              </div>
            </div>}
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      {homepage.intro !== false && homeSections.intro.enabled !== false && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h2 className="text-4xl md:text-5xl font-serif text-gray-900">
                {homeSections.intro.title}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed whitespace-pre-line">
                {homeSections.intro.description}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Services Grid */}
      {homepage.services !== false && homeSections.services.enabled !== false && serviceCards.length > 0 && (
      <section className="py-20 bg-sage-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">{homeSections.services.title}</h2>
            <p className="text-lg text-gray-600">{homeSections.services.description}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {serviceCards.map((card) => (
              <Card key={card.key} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white">
                <CardContent className="p-8 space-y-4">
                  <div className={`h-14 w-14 ${card.iconClass} rounded-2xl flex items-center justify-center`}>
                    {card.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{card.title}</h3>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line">{card.description}</p>
                  {card.href && card.label ? (card.to ? (
                    <Link to={card.to} className="inline-flex items-center text-sage-700 font-medium hover:text-sage-800 transition-colors">
                      {card.label} <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  ) : (
                    <a href={card.href} {...(card.newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})} className="inline-flex items-center text-sage-700 font-medium hover:text-sage-800 transition-colors">
                      {card.label} <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  )) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Testimonials */}
      {homepage.testimonials !== false && testimonials.length > 0 && (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">What Women Are Saying</h2>
            <p className="text-lg text-gray-600">Real stories from real women on their healing journey</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-0 shadow-lg bg-sage-50">
                <CardContent className="p-8 space-y-4">
                  <div className="flex space-x-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Heart key={i} className="h-5 w-5 fill-rose-500 text-rose-500" />
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed italic">"{testimonial.text}"</p>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* CTA Section */}
      {homepage.cta !== false && (
      <section className="py-20 bg-gradient-to-br from-sage-700 to-sage-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-serif">Ready to Begin Your Healing Journey?</h2>
          <p className="text-xl text-sage-100">
            Take the first step towards wellness. Book your session today and start your path to healing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {bookingEnabled && (
              <Link to="/booking">
                <Button size="lg" className="bg-white text-sage-800 hover:bg-sage-50 px-8 py-6 text-lg rounded-xl transition-all duration-300 hover:shadow-xl">
                  Book Your Session
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            )}
            <a href={siteInfo?.whatsapp || '#'} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl transition-all duration-300">
                Contact on WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
      )}
    </div>
  );
};

export default Home;
